import type { useGT } from 'gt-react';
import { NamespaceKeySchema } from '@erp/rpc';

/**
 * Localized "Name (KEY)" label for a namespace, shared by pickers, tables,
 * and badges so translators can reorder the parts.
 */
export function formatNamespaceLabel(
  gt: ReturnType<typeof useGT>,
  namespace: { name: string; key: string },
): string {
  return gt('{name} ({key})', {
    context: 'namespace label combining its name and short key prefix',
    name: namespace.name,
    key: namespace.key,
  });
}

/**
 * Validates against the namespaces contract's own key schema
 * (alphanumeric, 1-10 chars; the server uppercases on save).
 */
export function isValidNamespaceKey(key: string): boolean {
  return NamespaceKeySchema.safeParse(key).success;
}

/** Sentinel select value for "no assigned namespace". */
export const UNASSIGNED_NAMESPACE_VALUE = '__unassigned__';

export function namespaceIdFromSelectValue(value: string): string | null {
  return value === UNASSIGNED_NAMESPACE_VALUE ? null : value;
}

export function selectValueFromNamespaceId(
  namespaceId: string | null | undefined,
): string {
  return namespaceId ?? UNASSIGNED_NAMESPACE_VALUE;
}

export type NamespaceNoneOption = {
  id: string;
  label: string;
  display: string;
};

/** None-option for grouping pickers (workflows, agents): leave unassigned. */
export function unassignedNamespaceOption(
  gt: ReturnType<typeof useGT>,
): NamespaceNoneOption {
  return {
    id: UNASSIGNED_NAMESPACE_VALUE,
    label: gt('No assigned namespace', {
      context: 'namespace picker option to leave the record ungrouped',
    }),
    display: gt('None', {
      context: 'namespace picker short display when unassigned',
    }),
  };
}
